/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { Input, message } from 'antd';
import { PASSWORD_MAX_LENGTH, EMAIL_MAX_LENGTH } from '../../utils/helper';
import TitleBanner from '../../components/TitleBanner';
import { preventAction } from './Tools';
import { USER_LOGIN } from './translationValues';
import { LoginContainer, SubmitButton } from './styles';
import {
    ModalBody,
    ModalContainer,
    InputLabel,
    InputContainer
} from '../../components/Modals/styles';
import { signIn } from 'supertokens-web-js/recipe/emailpassword';
import { Icon } from 'antd';
import i18n from '../../i18n';

const NAMESPACES = ['USER_LOGIN'];

const Ldap = props => {
    const { t } = props;

    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    
    const resetButtonState = () => {
        setDisabled(false);
        setLoading(false);
    }

    // login
    async function signInClicked(email, password) {

        try {
            let response = await signIn({
                formFields: [
                    {
                        id: 'email',
                        value: email,
                    },
                    {
                        id: 'password',
                        value: password
                    },
                ]
            });

            if (response.status !== 'OK') {
                resetButtonState();
            }

            if (response.status === 'FIELD_ERROR') {
                response.formFields.forEach(formField => {
                    if (formField.id === 'email') {                       
                        // Email validation failed (for example incorrect email syntax).
                        message.error(formField.error);
                    }
                });
            } else if (response.status === 'WRONG_CREDENTIALS_ERROR') {
                message.error(t(USER_LOGIN.WRONG_CREDENTIAL_COMBINATION_ERROR_MESSAGE.key, USER_LOGIN.WRONG_CREDENTIAL_COMBINATION_ERROR_MESSAGE.value));
            } else if (response.status === 'GENERAL_ERROR') {
                message.error(t(USER_LOGIN.SOMETHING_WENT_WRONG_ERROR_MESSAGE.key, USER_LOGIN.SOMETHING_WENT_WRONG_ERROR_MESSAGE.value));

            } else {
                return response;
            }
        } catch (err) {
            resetButtonState();
            if (err.isSuperTokensGeneralError === true) {
                // this may be a custom error message sent from the API by you.
                console.log('error', err.message)
                message.error(err.message);
            } else {
                message.error(t(USER_LOGIN.GENERAL_ERROR_MESSAGE.key, USER_LOGIN.GENERAL_ERROR_MESSAGE.value));
            }
        }
    };

    // Code to check if all fields are empty
    // Return True if any  fields are empty, 
    const checkAllFields = () => {
        if (password.trim().length !== 0 && email.length !== 0) {
            return (false);
        } else {
            return (true);
        }
    ;}

    const handleKeyPress = e => {
        if ((!checkAllFields() && e.key === 'Enter')) {
            handleSignIn();
        }
    };

    const handleSignIn = async () => {
        if (loading || disabled) return;

        setLoading(true);
        setDisabled(true);

        await signInClicked(email, password);
    };

    useEffect(() => {
        if (!checkAllFields()) {
            setDisabled(false);
        } else {
            setDisabled(true);
        }
    }, [password, emailError, email]);

    const resetState = () => {
        setEmail('');
        setEmailError(false);
        setPassword('');
        setDisabled(true);
    };

    //  reset all state when we leave the page
    useEffect(() => {
        return () => {
            resetState();
        };
    }, []);

    useEffect(() => {
        //  The field can be prefilled on the very first page loading by the browser  
        const checkAutofilled = () => {
            const emailAutofilled = !!document.getElementById('emailInput')?.matches('*:-webkit-autofill');
            const passwordAutofilled = !!document.getElementById('passwordInput')?.matches('*:-webkit-autofill');
            emailAutofilled && passwordAutofilled && setDisabled(false);
        }
        // The time when it's ready is not very stable, so check few times
        setTimeout(checkAutofilled, 500);
        setTimeout(checkAutofilled, 1000);
        setTimeout(checkAutofilled, 1500)
        //Setting the application language to browser language
        setTimeout(() => {
            i18n.changeLanguage(navigator.language || navigator.userLanguage);
        }, 500);
    }, [])

    const renderLogin = () => (
        <>
            <InputContainer password>
                <InputLabel>{t(USER_LOGIN.LOGIN_USERNAME_LABEL.key, USER_LOGIN.LOGIN_USERNAME_LABEL.value)}</InputLabel>
                <Input
                    id='emailInput'
                    name="Email"
                    size="large"
                    placeholder={t(USER_LOGIN.LOGIN_USERNAME_PLACEHOLDER.key, USER_LOGIN.LOGIN_USERNAME_PLACEHOLDER.value)}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onPaste={preventAction}
                    onCopy={preventAction}
                    className="inputBox "
                    maxLength={EMAIL_MAX_LENGTH}
                />
            </InputContainer>
            <InputContainer password>
                <InputLabel>{t(USER_LOGIN.LOGIN_PASSWORD_LABEL.key, USER_LOGIN.LOGIN_PASSWORD_LABEL.value)}</InputLabel>
                <Input.Password
                    size="large"
                    id='passwordInput'
                    placeholder={t(USER_LOGIN.LOGIN_PASSWORD_PLACEHOLDER.key, USER_LOGIN.LOGIN_PASSWORD_PLACEHOLDER.value)}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onPaste={preventAction}
                    onCopy={preventAction}
                    maxLength={PASSWORD_MAX_LENGTH}
                />
            </InputContainer>
        </>
    );

    return (
            <LoginContainer onKeyDown={e => e.stopPropagation()} onKeyPress={handleKeyPress}>
                <TitleBanner
                    title={'PERFECT DOC STUDIO'}
                />
                <p className="usage">
                    {t(USER_LOGIN.LOGIN_USER_DESCRIPTION.key, USER_LOGIN.LOGIN_USER_DESCRIPTION.value)

                    }
                </p>
                <ModalBody className="content-body">
                    <ModalContainer>{renderLogin()}</ModalContainer>
                </ModalBody>
                <div className="footer">
                    <SubmitButton
                        key="submit"
                        type="primary"
                        disable={disabled}
                        onClick={handleSignIn}
                    >
                        <div className="wrap-1">
                            <div> {loading ? t(USER_LOGIN.LOGGING_IN_USER_BUTTON.key, USER_LOGIN.LOGGING_IN_USER_BUTTON.value) : t(USER_LOGIN.LOGIN_USER_BUTTON.key, USER_LOGIN.LOGIN_USER_BUTTON.value)}</div>
                            {loading ? <Icon type="loading" className="loading" /> : <div className="arrow"></div>}
                        </div>
                        <div className="wrap-2">
                            <div>
                                {t(USER_LOGIN.LOGIN_USER_BUTTON.key, USER_LOGIN.LOGIN_USER_BUTTON.value)}
                            </div>
                            <div className="arrow"></div>
                        </div>
                    </SubmitButton>
                </div>
            </LoginContainer>
    );
};

export default withTranslation(NAMESPACES)(Ldap);
